import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell,
} from 'recharts';
import { Loader2, Target, TrendingUp, UserX, Briefcase, AlertCircle } from 'lucide-react';
import { atlasApi, AtlasUser, DiscFit, DiscSugestao, DiscFator, PerfilDisc } from '../shared/atlasApi';

interface Props {
  user: AtlasUser;
  funccodi: number;
}

const DISC_CORES: Record<DiscFator, string> = { D: '#e11d48', I: '#f59e0b', S: '#10b981', C: '#3b82f6' };
const DISC_LABEL: Record<DiscFator, string> = { D: 'Dominância', I: 'Influência', S: 'Estabilidade', C: 'Conformidade' };
const FATORES: DiscFator[] = ['D', 'I', 'S', 'C'];

function corFit(pct: number): string {
  if (pct >= 75) return '#10b981';
  if (pct >= 50) return '#f59e0b';
  return '#e11d48';
}

// Gauge circular simples (SVG)
const GaugeFit: React.FC<{ pct: number }> = ({ pct }) => {
  const size = 140;
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, pct));
  const offset = c * (1 - v / 100);
  const cor = corFit(v);
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none" stroke={cor} strokeWidth={stroke}
          strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease' }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-black" style={{ color: cor }}>{Math.round(v)}%</span>
        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold">fit</span>
      </div>
    </div>
  );
};

const MsgStatus: React.FC<{ icon: React.ReactNode; titulo: string; texto: string }> = ({ icon, titulo, texto }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 flex items-start gap-3">
    <div className="text-amber-600 flex-shrink-0">{icon}</div>
    <div>
      <h3 className="font-bold text-amber-800">{titulo}</h3>
      <p className="text-sm text-amber-700 mt-1">{texto}</p>
    </div>
  </div>
);

const Fit: React.FC<Props> = ({ user, funccodi }) => {
  const [fit, setFit] = useState<DiscFit | null>(null);
  const [sugestao, setSugestao] = useState<DiscSugestao | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setErro(null);
    Promise.all([
      atlasApi.getDiscFit(user, funccodi),
      atlasApi.getDiscSugestaoCarreira(user, funccodi).catch(() => null),
    ])
      .then(([f, s]) => { if (!cancel) { setFit(f); setSugestao(s); } })
      .catch(e => { if (!cancel) setErro(e.message || 'Erro ao carregar fit'); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [user, funccodi]);

  if (loading) {
    return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando fit...</div>;
  }
  if (erro) {
    return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>;
  }
  if (!fit) return null;

  const sugestoes = sugestao?.sugestoes || [];

  // Bloco de sugestões de carreira (sempre que houver)
  const blocoSugestoes = sugestoes.length > 0 && (
    <div className="bg-white rounded-2xl border border-gray-200 p-4">
      <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
        <TrendingUp className="w-4 h-4 text-purple-600" /> Sugestões de carreira
      </h3>
      <ul className="space-y-2">
        {sugestoes.map(s => (
          <li key={s.codigo} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/50">
            <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center flex-shrink-0">
              <Briefcase className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-gray-900 truncate">{s.nome}</div>
              <div className="text-xs text-gray-400">
                {s.codigo}{s.departamento ? ` · ${s.departamento}` : ''}
              </div>
            </div>
            <div className="text-right">
              <div className="text-lg font-bold" style={{ color: corFit(s.fit_pct) }}>{Math.round(s.fit_pct)}%</div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wider">fit</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );

  // Tratamento de status
  if (fit.status === 'sem_disc') {
    return (
      <div className="space-y-4 max-w-3xl">
        <MsgStatus icon={<UserX className="w-6 h-6" />} titulo="Sem perfil DISC"
          texto="Este colaborador ainda não respondeu o questionário DISC. Sem ele não é possível calcular o fit com o cargo." />
        {blocoSugestoes}
      </div>
    );
  }
  if (fit.status === 'sem_cargo') {
    return (
      <div className="space-y-4 max-w-3xl">
        <MsgStatus icon={<Briefcase className="w-6 h-6" />} titulo="Sem cargo vinculado"
          texto="Este colaborador não está vinculado a um cargo cadastrado. Cadastre/associe um cargo em Perfis de cargo para ver o fit." />
        {blocoSugestoes}
      </div>
    );
  }
  if (fit.status === 'cargo_sem_perfil') {
    return (
      <div className="space-y-4 max-w-3xl">
        <MsgStatus icon={<AlertCircle className="w-6 h-6" />} titulo="Cargo sem perfil DISC ideal"
          texto={`O cargo ${fit.cargo?.nome || ''} ainda não tem um perfil DISC ideal definido. Defina-o em Perfis de cargo para calcular o fit.`} />
        {blocoSugestoes}
      </div>
    );
  }

  // status === 'ok'
  const real = fit.perfil_real;
  const ideal = fit.perfil_ideal;
  const barData = FATORES.map(f => ({
    fator: f,
    Real: real?.[f as keyof PerfilDisc] ?? 0,
    Ideal: ideal?.[f as keyof PerfilDisc] ?? 0,
    gap: fit.gaps?.[f as keyof PerfilDisc] ?? 0,
  }));

  return (
    <div className="space-y-4 max-w-3xl">
      {/* Score + cargo */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 flex flex-wrap items-center gap-6">
        <GaugeFit pct={fit.fit_pct ?? 0} />
        <div className="min-w-0 flex-1">
          <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold flex items-center gap-1">
            <Target className="w-3.5 h-3.5" /> Aderência ao cargo
          </div>
          <h2 className="text-xl font-bold text-gray-900">{fit.cargo?.nome || '—'}</h2>
          {fit.cargo?.codigo && <p className="text-sm text-gray-500">Código {fit.cargo.codigo}</p>}
          <p className="text-sm text-gray-600 mt-2">
            {(fit.fit_pct ?? 0) >= 75
              ? 'Excelente aderência entre o perfil comportamental e o esperado para o cargo.'
              : (fit.fit_pct ?? 0) >= 50
                ? 'Aderência moderada — há fatores com gap relevante a desenvolver.'
                : 'Baixa aderência — perfil comportamental distante do ideal do cargo.'}
          </p>
        </div>
      </div>

      {/* Real x Ideal */}
      <div className="bg-white rounded-2xl border border-gray-200 p-4">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Perfil real × ideal</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={barData}>
            <XAxis dataKey="fator" tick={{ fontSize: 13, fontWeight: 700 }} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="Real" radius={[6, 6, 0, 0]}>
              {barData.map(d => <Cell key={`r-${d.fator}`} fill={DISC_CORES[d.fator as DiscFator]} />)}
            </Bar>
            <Bar dataKey="Ideal" radius={[6, 6, 0, 0]} fill="#9ca3af" fillOpacity={0.55} />
          </BarChart>
        </ResponsiveContainer>

        {/* Tabela de gaps */}
        <table className="w-full text-sm mt-2">
          <thead className="text-[11px] text-gray-500 uppercase tracking-wider">
            <tr>
              <th className="text-left py-1">Fator</th>
              <th className="text-center py-1">Real</th>
              <th className="text-center py-1">Ideal</th>
              <th className="text-center py-1">Gap</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {barData.map(d => (
              <tr key={d.fator}>
                <td className="py-2">
                  <span className="inline-flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: DISC_CORES[d.fator as DiscFator] }} />
                    <span className="text-gray-800">{DISC_LABEL[d.fator as DiscFator]}</span>
                  </span>
                </td>
                <td className="text-center text-gray-700">{d.Real}%</td>
                <td className="text-center text-gray-700">{d.Ideal}%</td>
                <td className={`text-center font-semibold ${d.gap >= 30 ? 'text-rose-600' : d.gap >= 15 ? 'text-amber-600' : 'text-emerald-600'}`}>{d.gap}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {blocoSugestoes}
    </div>
  );
};

export default Fit;
